import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useNotify from '@/hooks/useNotify';
import { useAppDispatch } from '@/data/store/utils/redux';
import { createOrganizationAsync, storeOrganizationId } from '@/data/store/actions';
import { TOrganizationPayload } from '@/data/types';
import CreateOrganizationStepOneCard from './components/CreateOrganizationStepOneCard';
import CreateOrganizationStepTwoCard from './components/CreateOrganizationStepTwoCard';
import CreateOrganizationStepThreeCard from './components/CreateOrganizationStepThreeCard';
import CreateOrganizationStepOneForm from './components/CreateOrganizationStepOneForm';
import CreateOrganizationStepTwoForm from './components/CreateOrganizationStepTwoForm';
import CreateOrganizationStepThreeForm from './components/CreateOrganizationStepThreeForm';

interface OrganizationData {
  name: string;
  description: string;
  industry: string;
  size: string;
  location: string;
  energyGoals: string;
  teamSize: string;
  contactEmail: string;
  contactPhone: string;
}

const CreateOrganization: React.FC = () => {
  const notify = useNotify();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [currentStep, setCurrentStep] = useState(1);
  const [isCreating, setIsCreating] = useState(false);
  const [organizationData, setOrganizationData] = useState<OrganizationData>({
    name: '',
    description: '',
    industry: '',
    size: '',
    location: '',
    energyGoals: '',
    teamSize: '',
    contactEmail: '',
    contactPhone: '',
  });

  const steps = [
    { number: 1, title: 'Βασικές Πληροφορίες', description: 'Στοιχεία οργανισμού' },
    { number: 2, title: 'Διαμόρφωση', description: 'Ρυθμίσεις και στόχοι' },
    { number: 3, title: 'Επιβεβαίωση', description: 'Ανασκόπηση και ολοκλήρωση' },
  ];

  const updateOrganizationData = (data: Partial<OrganizationData>) => {
    setOrganizationData((prev) => ({ ...prev, ...data }));
  };

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleBack = () => {
    navigate('/organizations');
  };

  const handleComplete = async () => {
    if (isCreating) return;
    
    setIsCreating(true);
    
    try {
      // Create the payload according to TOrganizationPayload type
      const payload: TOrganizationPayload = {
        name: organizationData.name,
        description: organizationData.description || undefined,
        image: undefined, // Mock for now
        cover: undefined, // Mock for now
        contact_email: organizationData.contactEmail || undefined,
        contact_phone: organizationData.contactPhone || undefined,
        website_url: undefined, // Mock for now
      };

      // Create the organization
      const result = await dispatch(createOrganizationAsync({ payload }));
      
      if (createOrganizationAsync.fulfilled.match(result)) {
        const newOrganization = result.payload;
        
        // Store the organization ID
        dispatch(storeOrganizationId(newOrganization.id));
        
        // Show success message
        notify.success(`Καλώς ήρθατε στον ${newOrganization.name}!`);

        // Navigate to dashboard after a short delay
        setTimeout(() => {
          navigate('/dashboard');
        }, 1500);
      } else {
        // Handle error case
        notify.error('Σφάλμα κατά τη δημιουργία του οργανισμού');
        setIsCreating(false);
      }
    } catch (error) {
      console.error('Failed to create organization:', error);
      notify.error('Σφάλμα κατά τη δημιουργία του οργανισμού');
      setIsCreating(false);
    }
  };

  const isStepValid = (step: number): boolean => {
    switch (step) {
      case 1:
        return !!(
          organizationData.name &&
          organizationData.description &&
          organizationData.industry &&
          organizationData.size
        );
      case 2:
        return !!(
          organizationData.location &&
          organizationData.energyGoals &&
          organizationData.teamSize &&
          organizationData.contactEmail
        );
      case 3:
        return true;
      default:
        return false;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Main Content */}
      <div className="flex h-screen">
        {/* Left Side - Illustration - Hidden on tablet and mobile */}
        <div className="hidden h-full w-1/2 items-center justify-center lg:flex">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
              className="h-full w-full"
            >
              {currentStep === 1 && <CreateOrganizationStepOneCard />}
              {currentStep === 2 && <CreateOrganizationStepTwoCard />}
              {currentStep === 3 && <CreateOrganizationStepThreeCard />}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Right Side - Form - Full width on tablet and mobile */}
        <div className="flex w-full items-center justify-center bg-white lg:w-1/2">
          <div className="h-full w-full">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="h-full"
              >
                {currentStep === 1 && (
                  <CreateOrganizationStepOneForm
                    organizationData={organizationData}
                    updateOrganizationData={updateOrganizationData}
                    onNextStep={handleNext}
                  />
                )}
                {currentStep === 2 && (
                  <CreateOrganizationStepTwoForm
                    organizationData={organizationData}
                    updateOrganizationData={updateOrganizationData}
                    onNextStep={handleNext}
                  />
                )}
                {currentStep === 3 && (
                  <CreateOrganizationStepThreeForm 
                    organizationData={organizationData} 
                    onComplete={handleComplete}
                    isCreating={isCreating}
                  />
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateOrganization;